import React from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../../../components/ui/dropdown-menu';
import {
  BILLING_STATUS_LABELS,
  BILLING_STATUS_COLORS,
  BILLING_STATUS_TRANSITIONS,
} from '../types/billing.types';
import type { BillingStatus } from '../types/billing.types';

interface BillingStatusBadgeProps {
  status: BillingStatus;
  isNonBillable: boolean;
  entityType: 'time-entry' | 'expense';
  entityId: string;
  onTransition: (id: string, newStatus: BillingStatus) => Promise<void>;
}

const BillingStatusBadge: React.FC<BillingStatusBadgeProps> = ({
  status,
  isNonBillable,
  entityId,
  onTransition,
}) => {
  if (isNonBillable) {
    return (
      <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
        Non-billable
      </span>
    );
  }

  const validTargets = BILLING_STATUS_TRANSITIONS[status];
  const colors = BILLING_STATUS_COLORS[status];
  const badgeClasses = `inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${colors.bg} ${colors.text} ${colors.darkBg} ${colors.darkText}`;

  // No valid transitions (e.g., billed) → display-only badge
  if (validTargets.length === 0) {
    return (
      <span className={badgeClasses}>
        {BILLING_STATUS_LABELS[status]}
      </span>
    );
  }

  // Has transitions → interactive dropdown badge
  const destructiveStatuses: BillingStatus[] = ['written_off'];
  const normalTargets = validTargets.filter((s) => !destructiveStatuses.includes(s));
  const destructiveTargets = validTargets.filter((s) => destructiveStatuses.includes(s));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <span className={`${badgeClasses} cursor-pointer hover:ring-1 hover:ring-current/30 transition-all`}>
          {BILLING_STATUS_LABELS[status]}
          <ChevronDownIcon className="h-3 w-3 opacity-60" />
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center">
        <div className="px-2 py-1 text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
          Change status to
        </div>
        {normalTargets.map((target) => {
          const tc = BILLING_STATUS_COLORS[target];
          return (
            <DropdownMenuItem
              key={target}
              onClick={() => onTransition(entityId, target)}
            >
              <span className={`inline-block w-2 h-2 rounded-full mr-2 ${tc.bg}`} />
              {BILLING_STATUS_LABELS[target]}
            </DropdownMenuItem>
          );
        })}
        {destructiveTargets.length > 0 && (
          <>
            <DropdownMenuSeparator />
            {destructiveTargets.map((target) => (
              <DropdownMenuItem
                key={target}
                onClick={() => onTransition(entityId, target)}
                className="text-red-600 dark:text-red-400"
              >
                {BILLING_STATUS_LABELS[target]}
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default BillingStatusBadge;
